/**
 * EdaConnectDialog — modal launched from the Instrument Rack's EDA
 * segment. One-stop view of "everything you need to wire this project
 * to a desktop EDA tool": sync status banner, project ID copy, API key
 * shortcut, and the latest plugin installer.
 *
 * Mirrors `PluginSyncCard` (project Settings tab), but stripped of the
 * outer card chrome since the Dialog already provides that — and with
 * an Altium-coming-soon row that the project page omits.
 */
import {
  CableRounded,
  CheckCircleRounded,
  ContentCopyRounded,
  KeyRounded,
  RadioButtonUncheckedRounded,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { toast } from "@stores/toastStore";
import { pluginSyncApi } from "../services/api";
import { pluginKeys } from "../services/queryKeys";
import { EasyEdaPluginInstallCard } from "./EasyEdaPluginInstallCard";
import { KicadPluginInstallCard } from "./KicadPluginInstallCard";

function formatRelative(iso: string | null): string {
  if (!iso) return "never";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 60_000) return "just now";
  if (diffMs < 3600_000) return `${Math.round(diffMs / 60_000)} min ago`;
  if (diffMs < 86_400_000) return `${Math.round(diffMs / 3600_000)} h ago`;
  if (diffMs < 30 * 86_400_000) {
    return `${Math.round(diffMs / 86_400_000)} d ago`;
  }
  return date.toLocaleDateString();
}

interface EdaConnectDialogProps {
  open: boolean;
  onClose: () => void;
  /** Active project UUID. Null when the user has no project pinned —
   *  the dialog still opens (so they can grab the install / docs) but
   *  the per-project sections are replaced with a hint. */
  projectId: string | null;
}

export function EdaConnectDialog({
  open,
  onClose,
  projectId,
}: EdaConnectDialogProps) {
  const statusQuery = useQuery({
    queryKey: pluginKeys.syncStatus(projectId),
    queryFn: () => {
      if (!projectId) throw new Error("projectId is required");
      return pluginSyncApi.getStatus(projectId);
    },
    enabled: open && Boolean(projectId),
    staleTime: 30 * 1000,
  });

  const copy = async () => {
    if (!projectId) return;
    try {
      await navigator.clipboard.writeText(projectId);
      toast.success("Project ID copied.");
    } catch {
      toast.error("Couldn't access the clipboard.");
    }
  };

  const status = statusQuery.data;
  const hasSynced = (status?.push_count ?? 0) > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CableRounded fontSize="small" color="action" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div">
              Connect EDA tools
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Push and pull this project from KiCad or EasyEDA — desktop plugin
              or browser extension.
            </Typography>
          </Box>
          <Chip label="Beta" size="small" color="primary" variant="outlined" />
        </Stack>
      </DialogTitle>

      <DialogContent>
        {projectId ? (
          <Box sx={{ mt: 1 }}>
            {statusQuery.isLoading ? (
              <Skeleton variant="rounded" height={36} />
            ) : statusQuery.isError ? (
              <Alert severity="info" variant="outlined">
                Couldn't read sync status — the connection still works, this
                banner just doesn't.
              </Alert>
            ) : hasSynced ? (
              <Alert
                severity="success"
                icon={<CheckCircleRounded fontSize="small" />}
                variant="outlined"
              >
                Last synced {formatRelative(status?.last_pushed_at ?? null)} via
                the KiCad plugin · {status?.push_count} push
                {(status?.push_count ?? 0) === 1 ? "" : "es"}
                {status?.last_snapshot_id && (
                  <>
                    {" "}
                    ·{" "}
                    <Link
                      to="/projects/$projectId/snapshots/$snapshotId"
                      params={{
                        projectId,
                        snapshotId: status.last_snapshot_id,
                      }}
                      onClick={onClose}
                    >
                      Review latest
                    </Link>
                  </>
                )}
              </Alert>
            ) : (
              <Alert
                severity="info"
                icon={<RadioButtonUncheckedRounded fontSize="small" />}
                variant="outlined"
              >
                Not connected yet. Install the plugin (below) and push from
                KiCad — pushes will land here for review.
              </Alert>
            )}
          </Box>
        ) : (
          <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
            Pin a project from the sidebar to see its sync status and project
            ID. You can still install the plugin from here.
          </Alert>
        )}

        {projectId && (
          <>
            <Divider sx={{ my: 2 }} />

            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: 96 }}
                >
                  Project ID
                </Typography>
                <Box
                  sx={{
                    fontFamily: "monospace",
                    fontSize: 13,
                    flex: 1,
                    overflow: "auto",
                    whiteSpace: "nowrap",
                  }}
                >
                  {projectId}
                </Box>
                <Tooltip title="Copy">
                  <IconButton size="small" onClick={copy}>
                    <ContentCopyRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: 96 }}
                >
                  API key
                </Typography>
                <Button
                  component={Link}
                  to="/settings/api-keys"
                  size="small"
                  variant="outlined"
                  startIcon={<KeyRounded fontSize="small" />}
                  onClick={onClose}
                >
                  Manage keys
                </Button>
              </Stack>
            </Stack>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2}>
          <KicadPluginInstallCard compact />
          <EasyEdaPluginInstallCard compact />
        </Stack>

        <Box
          sx={(t) => ({
            mt: 2,
            p: 1.5,
            borderRadius: 1,
            border: `1px dashed ${t.palette.divider}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          })}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            <strong>Altium</strong> support is on the roadmap — same push / pull
            model, different host.
          </Typography>
          <Chip label="Soon" size="small" variant="outlined" />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
